/**
 * @file locales.ts
 * @description THE single source of truth for the locales the frontend offers.
 *
 * Before SE-6505 three independent lists were live: `LANGUAGE_LABELS` here
 * (order En Es Fr De Pt It), `SUPPORTED_SUMMARY_LOCALES` in the AI summary hook
 * (order En Es Pt Fr De It), and Settings, which rendered the backend
 * `supportedLocales` query raw — all twenty enum values. A user could pick
 * Japanese in Settings and then see `Generate · ja_JP` on a summary with no
 * matching menu entry.
 *
 * Every offered-locale surface now reads {@link SUPPORTED_UI_LOCALES} (or the
 * paired {@link SUPPORTED_UI_LOCALE_OPTIONS}) so there is exactly one list and
 * exactly one ordering. Settings *filters* the backend list through it rather
 * than rendering it raw, so the backend enum stays at twenty values with no
 * Postgres migration and no invalidated rows — adding locale #7 later is a
 * one-line change here.
 * @module constants/locales
 */

/*
 * A TYPE-only import on purpose. This module is imported by prompt builders,
 * pickers and hooks whose tests mock `@/generated/graphql`; a value import of
 * the enum would make every one of those mocks have to carry it, and a mock
 * that forgot would blow up at module scope rather than in a test body. The
 * `satisfies` clauses below still bind every literal here to the generated
 * enum at compile time, which is what R11 asks for — no new taxonomy, and a
 * locale the backend drops fails typecheck here.
 */
import type { SupportedLocale } from "@/generated/graphql";

/** The enum's VALUE union — what a locale code is, as a string type. */
type LocaleCode = `${SupportedLocale}`;

/**
 * The locales the UI offers, in the one canonical order every surface uses.
 *
 * Six by product decision (PRD "Language Preference Wiring" amendment D1),
 * which supersedes the earlier "all 20 SupportedLocale values" wording.
 * The backend enum is deliberately NOT narrowed — see the module doc.
 */
export const SUPPORTED_UI_LOCALES = [
  "en_US",
  "es_ES",
  "pt_BR",
  "fr_FR",
  "de_DE",
  "it_IT",
] as const satisfies readonly LocaleCode[];

/** One of the locales the UI offers. */
export type UiLocale = (typeof SUPPORTED_UI_LOCALES)[number];

/** The locale used when nothing better can be resolved. */
export const DEFAULT_UI_LOCALE: UiLocale = "en_US";

/**
 * The one display-name lookup, keyed by every offered locale — so a lookup on a
 * member of {@link SUPPORTED_UI_LOCALES} is total and needs no fallback.
 */
export const LOCALE_LABELS: Readonly<Record<UiLocale, string>> = {
  en_US: "English",
  es_ES: "Spanish",
  pt_BR: "Portuguese",
  fr_FR: "French",
  de_DE: "German",
  it_IT: "Italian",
};

/** A locale as offered to the user: the code plus its display label. */
export interface LocaleOption {
  readonly value: UiLocale;
  readonly label: string;
}

/**
 * The offered locales as `{ value, label }` pairs in canonical order — the
 * shape pickers consume directly.
 */
export const SUPPORTED_UI_LOCALE_OPTIONS: readonly LocaleOption[] =
  SUPPORTED_UI_LOCALES.map(value => ({
    value,
    label: LOCALE_LABELS[value],
  }));

/**
 * True when the code is one of the locales the UI offers.
 * @param localeCode - A locale code, or nothing
 * @returns Whether the code is in {@link SUPPORTED_UI_LOCALES}
 */
export function isSupportedUiLocale(
  localeCode: string | null | undefined
): localeCode is UiLocale {
  return (SUPPORTED_UI_LOCALES as readonly string[]).includes(
    localeCode as string
  );
}

/**
 * Resolves a stored `languagePreference` to the offered locale that should be
 * DISPLAYED for it. Three rungs:
 *
 * 1. the preference itself, when it is one of the six;
 * 2. a same-language sibling inside the six (`en_GB`→`en_US`, `es_MX`→`es_ES`,
 *    `pt_PT`→`pt_BR`) — a Portuguese-Portugal user degrading to Brazilian
 *    Portuguese rather than English is materially better;
 * 3. {@link DEFAULT_UI_LOCALE}, for the eleven with no sibling in the six.
 *
 * `languagePreference` is NOT NULL on the backend, so live users are stored on
 * the fourteen dropped locales. Resolution is **display-only** — callers must
 * never write the resolved value back to the profile. Silently rewriting user
 * data to satisfy a display concern is not acceptable and would invalidate rows
 * the register requires stay valid.
 * @param preference - The stored languagePreference, a bare subtag ("pt"), or nothing
 * @returns One of {@link SUPPORTED_UI_LOCALES}
 */
export function resolveDisplayLocale(
  preference: string | null | undefined
): UiLocale {
  return findSupportedUiLocale(preference) ?? DEFAULT_UI_LOCALE;
}

/**
 * The un-collapsed half of {@link resolveDisplayLocale}: the offered locale that
 * genuinely matches `preference`, or `undefined` when none does.
 *
 * `resolveDisplayLocale` folds two different situations into `en_US` — a viewer
 * who actually asked for English, and a viewer stranded on one of the fourteen
 * dropped locales with no sibling in the six. For *display* that collapse is
 * correct and deliberate: something has to render. For any caller that must
 * keep those apart — SE-6510 auto-translates only for a viewer whose language
 * we can genuinely serve, and must never treat a stranded `ja_JP` viewer as an
 * English one — this is the primitive to use. `undefined` means "there is no
 * offered locale for this viewer", which is a distinct fact from `en_US`.
 *
 * SE-6508's `classifyLocaleSelection` below is the other caller, for the same
 * reason: a picker must not mark English for a viewer stranded on a locale it
 * no longer offers.
 * @param preference - The stored languagePreference, a bare subtag ("pt"), or nothing
 * @returns The matching offered locale, or undefined when there is none
 */
export function findSupportedUiLocale(
  preference: string | null | undefined
): UiLocale | undefined {
  if (!preference) return undefined;
  if (isSupportedUiLocale(preference)) return preference;

  const subtag = preference.split("_")[0]?.toLowerCase() ?? "";
  if (!subtag) return undefined;
  return SUPPORTED_UI_LOCALES.find(
    locale => locale.split("_")[0]?.toLowerCase() === subtag
  );
}

/**
 * The three states a locale selection can be in, kept apart on purpose.
 *
 * A picker that collapses these renders a lie in two directions: it marks
 * English for a user who never chose English, and it marks a default for a
 * user whose profile simply has not loaded. SE-6509 and SE-6510 both kept the
 * distinction; SE-6508 makes it the shared vocabulary every picker reads.
 */
export type LocaleSelection =
  /** Nothing is selected — the value is absent, not yet loaded, or "original". */
  | { readonly status: "unresolved" }
  /** The selection is one of the six the UI offers. */
  | { readonly status: "offered"; readonly locale: UiLocale }
  /**
   * The selection is a stored locale outside the six. `resolved` is the
   * offered locale the app will actually act in — display-only, never written
   * back — and is deliberately NOT treated as a selection the user made.
   */
  | {
      readonly status: "stranded";
      readonly code: string;
      readonly resolved: UiLocale;
    };

/**
 * Classifies a stored locale code into one of the three {@link LocaleSelection}
 * states.
 * @param preference - A stored languagePreference, a bare subtag, or nothing
 * @returns The state the selection is in
 */
export function classifyLocaleSelection(
  preference: string | null | undefined
): LocaleSelection {
  if (!preference) return { status: "unresolved" };
  if (isSupportedUiLocale(preference)) {
    return { status: "offered", locale: preference };
  }
  return {
    status: "stranded",
    code: preference,
    resolved: resolveDisplayLocale(preference),
  };
}

/**
 * Returns the display label for a locale code, resolving stranded locales
 * first so no surface can ever render a raw code like `ja_JP` (SE-4437).
 * @param localeCode - A locale code, a bare subtag, or nothing
 * @returns The display label of the resolved offered locale, e.g. "English"
 */
export function getLanguageLabel(
  localeCode: string | null | undefined
): string {
  return LOCALE_LABELS[resolveDisplayLocale(localeCode)];
}

/**
 * Resolves the locale a CAPTURE surface should operate in — today that is
 * speech recognition in `useDictation`, and it is deliberately the SAME rule
 * as {@link resolveDisplayLocale} rather than a parallel one.
 *
 * SE-6505 narrowed display only, and said so explicitly. That was right at the
 * time, but it left a split that SE-6509 owns: the Settings row rendered
 * `Dictation: English` for a user stored on `ja_JP` while `useDictation` fed
 * the raw `ja_JP` to the microphone. Once that row is relabelled to the app's
 * one **Language** control (R8, "visibly drives dictation"), a row that reads
 * "English" while the microphone listens in Japanese is simply false — and the
 * user cannot correct it, because Japanese is no longer an offered option.
 *
 * So behaviour resolves too. What does NOT change is the stored value: this is
 * still read-only resolution, nothing is written back, and a stranded profile
 * stays intact for the day a seventh locale is added. Note the first rung of
 * {@link resolveDisplayLocale} means most stranded users get a BETTER input
 * locale, not a worse one (`pt_PT`→`pt_BR`, `es_MX`→`es_ES`, `en_GB`→`en_US`);
 * only genuinely-dropped languages fall to English, and those users already
 * read English on every other surface.
 * @param preference - The stored `languagePreference`, or nothing
 * @param deviceLocale - The device-derived locale to use when nothing is stored
 *   (SE-4235 added that fallback; narrowing it here does not remove it)
 * @returns One of {@link SUPPORTED_UI_LOCALES}
 */
export function resolveInputLocale(
  preference: string | null | undefined,
  deviceLocale: string | null | undefined
): UiLocale {
  return resolveDisplayLocale(
    preference !== null && preference !== undefined && preference !== ""
      ? preference
      : deviceLocale
  );
}

/**
 * Narrows a backend `supportedLocales` list to the offered set, in canonical
 * order. Settings uses this instead of rendering the query result raw.
 *
 * When the query has not resolved, failed, or was skipped, the full offered set
 * is returned — the menu must not shrink because a network call is in flight.
 * @param backendLocales - Whatever the `supportedLocales` query returned, if anything
 * @returns The offered locales, always in canonical order and never empty
 */
export function narrowToSupportedUiLocales(
  backendLocales: readonly string[] | null | undefined
): readonly UiLocale[] {
  if (!backendLocales || backendLocales.length === 0) {
    return SUPPORTED_UI_LOCALES;
  }
  const offered = SUPPORTED_UI_LOCALES.filter(locale =>
    backendLocales.includes(locale)
  );
  return offered.length > 0 ? offered : SUPPORTED_UI_LOCALES;
}
